import React, { useEffect, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { Loader } from '../../components'
import { Button, buttonVariants } from '../../components/shadcn/Button'
import { cn } from '../../components/shadcn/utils'
import { useCore, useLogin } from '../../hooks'
import { toastInfo, toastError } from '../../lib/toastHelpers'
import { DisposalTable } from './DisposalTable'
import { graphql } from '../../gql'
import { useGQLInfinite, useGQLMutation } from '../../hooks/GQLUtilityHooks'
import { useQueryClient } from '@tanstack/react-query'
import { useIntersect } from '../../lib/hooks/useIntersect'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding, faPlus, faSearch, faTruck, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { Combox } from '../../components/shadcn/combox'
import { useDisposalFilters } from '../../lib/hooks/useDisposalFilters'
import { CarrierSelector } from '../../components/CarrierSelector'

const DisposalListQuery = graphql(`
  query DisposalsList($page: Int!, $filters: disposalFilters, $sort: String, $direction: String) {
    disposals(page: $page, filters: $filters, sort: $sort, direction: $direction) {
      items {
        id
        ...DisposalTable_DisposalFragment
        sent
        company {
          id
        }
        packages {
          id
        }
      }
      paginator {
        current
        totalCount
        endPage
      }
    }
  }
`)

const SendDisposalMutation = graphql(`
  mutation SendDisposal($id: String!) {
    sendDisposal(id: $id) {
      data {
        id
      }
      errors
    }
  }
`)

export const DisposalList: React.FC = () => {
  const { core, isFetching, setIsFetching } = useCore()
  const { measureRef, isIntersecting } = useIntersect()
  const queryClient = useQueryClient()
  const {
    selectedCarrierId,
    setSelectedCarrierId,
    selectedChildCompanyId,
    setSelectedChildCompany,
    selectedSent,
    setSelectedSent,
    apiFilters,
    resetFilters,
  } = useDisposalFilters()

  const { user } = useLogin()

  const childCompanies = user.company.children || []

  const { sort, direction } = useMemo(() => {
    return { sort: 'created', direction: 'DESC' }
  }, [])

  // Build GraphQL-compatible filters (server supports only carrier for disposals)
  const gqlFilters = useMemo(() => apiFilters, [apiFilters])

  const {
    data,
    isLoading,
    isFetching: isQueryFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useGQLInfinite<any>(DisposalListQuery as any, ['packageDisposals'], {
    page: 1,
    filters: gqlFilters,
    sort,
    direction,
  })

  const { mutateAsync: mutateSendDisposal } = useGQLMutation(SendDisposalMutation, ['sendDisposal'])

  // Limit caching to prevent memory issues
  useEffect(() => {
    queryClient.setQueriesData(
      { queryKey: ['packageDisposals'], type: 'inactive' },
      (data: { pages: any[]; pageParams: any[] }) => {
        return {
          pages: (data?.pages || []).slice(0, 2),
          pageParams: (data?.pageParams || []).slice(0, 2),
        }
      },
    )
  }, [isQueryFetching, queryClient])

  useEffect(() => {
    if (isIntersecting && hasNextPage) {
      fetchNextPage()
    }
  }, [isIntersecting, hasNextPage, fetchNextPage])

  const sendDisposal = async (disposal: any) => {
    if (!core) return

    setIsFetching(true)
    try {
      await mutateSendDisposal({ id: disposal.id })
      toastInfo('Svoz byl úspěšně odeslán')
      queryClient.invalidateQueries({ queryKey: ['packageDisposals'] })
    } catch (error: any) {
      toastError('Chyba při odesílání svozu: ' + error.message)
    }
    setIsFetching(false)
  }

  const handleRefresh = () => {
    refetch()
  }

  // Client-side post-filtering for UI (sent/company), which BE does not support on disposals
  const filteredData = useMemo(() => {
    let items = data
    if (selectedSent !== 'all') {
      items = items.filter(d => (selectedSent === 'sent' ? !!d.sent : !d.sent))
    }
    return items
  }, [data, selectedSent])

  return (
    <div className="h-full">
      {/* Header with actions */}
      {/*<div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">Svozy balíků</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isQueryFetching}
            icon={['fas', 'refresh'] as any}
            title="Obnovit">
            Obnovit
          </Button>
        </div>

        <NavLink
          to="/disposal/create"
          className={cn(buttonVariants({ variant: 'default' }))}
          title="Vytvořit nový svoz">
          Vytvořit nový svoz
        </NavLink>
      </div>*/}

      {/* Filters */}
      <div className="orders-filters bg-white p-4 flex justify-center">
        <div className="grid grid-cols-7 gap-2 items-end w-[1400px]">
          {childCompanies.length > 0 && (
            <div className="form-control gap-1">
              <label className="flex items-center gap-2">
                <FontAwesomeIcon icon={faBuilding} /> Firma
              </label>
              <Combox
                items={[
                  { value: 'all', label: 'Vše' },
                  ...childCompanies.map(c => ({ value: String(c.id), label: c.name })),
                ]}
                placeholder="Vyberte firmu"
                value={selectedChildCompanyId ? String(selectedChildCompanyId) : 'all'}
                onValueChange={value => setSelectedChildCompany(value)}
              />
            </div>
          )}

          <div className="form-control gap-1">
            <label className="flex items-center gap-2">
              <FontAwesomeIcon icon={faTruck} /> Dopravce
            </label>
            <CarrierSelector
              mode="single"
              value={selectedCarrierId}
              onValueChange={setSelectedCarrierId}
              placeholder="Všichni"
            />
          </div>

          <div className="form-control gap-1">
            <label className="flex items-center gap-2"> Stav </label>
            <Combox
              items={[
                { value: 'all', label: 'Všechny' },
                { value: 'pending', label: 'Neodesláno' },
                { value: 'sent', label: 'Odesláno' },
              ]}
              value={selectedSent}
              onValueChange={value => setSelectedSent(value as any)}
            />
          </div>

          <div className="col-span-3 flex items-start gap-2">
            <Button
              onClick={resetFilters}
              variant="outlineDanger"
              size="lg"
              title="Vymazat filtry"
              icon={faXmark}></Button>
            <Button
              onClick={() => refetch()}
              variant="default"
              size="lg"
              title="Vyhledat"
              icon={faSearch}
              className="w-48">
              Vyhledat
            </Button>
          </div>
          {(childCompanies || [])?.length <= 0 && <div className="col-span-1" />}
          <NavLink
            to="/disposal/create"
            className={cn(buttonVariants({ variant: 'default' }), 'col-span-1 bg-green')}
            title="Vytvořit nový svoz">
            <FontAwesomeIcon icon={faPlus} />
            Vytvořit nový svoz
          </NavLink>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white flex items-center flex-col pb-4">
        {filteredData.length > 0 && !isLoading ? (
          <>
            <DisposalTable
              disposals={filteredData}
              onSendDisposal={sendDisposal}
              isLoading={isFetching || isQueryFetching}
            />
            {hasNextPage && (
              <div className="p-4 border-t">
                <Button
                  ref={measureRef}
                  variant={isFetchingNextPage ? 'disabled' : 'outline'}
                  className="w-full"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}>
                  {isFetchingNextPage ? (
                    <div className="flex items-center gap-2">
                      <div className="loader-small"></div>
                      Načítání...
                    </div>
                  ) : (
                    'Načíst další'
                  )}
                </Button>
              </div>
            )}
          </>
        ) : !isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="mb-4">
              <i className="fas fa-inbox text-4xl"></i>
            </div>
            <div className="text-lg font-medium mb-2">Žádné svozy nenalezeny</div>
            <div className="text-sm">
              {selectedCarrierId || selectedSent !== 'all'
                ? 'Pro zadané filtry nebyly nalezeny žádné svozy.'
                : 'Zatím nejsou k dispozici žádné svozy.'}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      {isLoading && data.length === 0 && (
        <div className="flex h-full items-center justify-center">
          <Loader size="fill-container" />
        </div>
      )}

      {/* Loading overlay for mutations */}
      {isFetching && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <Loader size="fill-container" />
            <div className="mt-2 text-sm text-gray-600">Zpracování...</div>
          </div>
        </div>
      )}
    </div>
  )
}
